import { useEffect, useId, useState } from 'react'
import { ActionIcon, Button, Indicator, Menu, ScrollArea, Text } from '@mantine/core'
import {
  IconBell,
  IconChecks,
  IconPrescription,
  IconSignature,
  IconVideo,
  IconVideoFilled,
  IconX,
} from '@tabler/icons-react'
import { showNotification } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import { formatNoteTimestamp } from '@/pages/Boards/time'
import { fmtTime } from '@/pages/Schedule/components/scheduleCalendar.tokens'
import {
  useTaskNotifications,
  type DoseSpotNotificationCounts,
  type TaskNotification,
} from '@/hooks/useTaskNotifications'
import { claimDoseSpotLaunch, releaseDoseSpotLaunch } from '@/utils/doseSpotLaunchLock'
import { cn } from '@/utils/cn'
import { DoseSpotNotificationsModal } from './DoseSpotNotificationsModal'

type PatientReadyNotification = Extract<TaskNotification, { kind: 'patient-ready' }>

// Letter signatures (PD-1407). These deep-link to a patient chart rather than a
// board item, so they render as their own row and are excluded from the task-shaped
// branches below.
const LETTER_KINDS = ['letter-signature-requested', 'letter-signed'] as const
type LetterNotification = Extract<TaskNotification, { kind: (typeof LETTER_KINDS)[number] }>
const isLetterNotification = (n: TaskNotification): n is LetterNotification =>
  (LETTER_KINDS as readonly string[]).includes(n.kind)
const LETTER_WORDING: Record<LetterNotification['kind'], string> = {
  'letter-signature-requested': 'asked you to review and sign',
  'letter-signed': 'signed',
}

function formatSentAt(iso: string): string {
  const relative = formatNoteTimestamp(iso)
  return relative === 'just now' ? 'Now' : relative
}

// Deep link that opens the assigned task: the Care Queue tab, the task's board,
// and the task drawer (via the `item` param CareCoordinationPage reads).
function taskLink(
  n: Exclude<TaskNotification, PatientReadyNotification | LetterNotification>,
): string {
  const params = new URLSearchParams({ tab: 'care-queue', board: n.boardId, item: n.itemId })
  return `/appointments?${params.toString()}`
}

/**
 * The badge counts **all four DoseSpot axes**, which gives count parity with DoseSpot's own
 * red bell — so BeHuman never disagrees with the system the provider is about to open.
 *
 * > Accepted risk: if `pendingPrescriptions` includes drafts a prescriber deliberately parks,
 * > the badge is permanently non-zero. Excluding pending was the considered alternative;
 * > parity won. Worth revisiting if staging shows pending behaving as a parking lot.
 */
function doseSpotTotal(counts: DoseSpotNotificationCounts | undefined): number {
  if (!counts) return 0
  return (
    counts.pendingPrescriptions +
    counts.transmissionErrors +
    counts.refillRequests +
    counts.changeRequests
  )
}

/**
 * The non-zero axes, in order of how much they need a prescriber: **a transmission error means
 * a prescription never reached the pharmacy**, so it leads; a parked draft is last.
 */
function doseSpotBreakdown(counts: DoseSpotNotificationCounts): string {
  const axes: [number, string][] = [
    [counts.transmissionErrors, 'transmission error'],
    [counts.refillRequests, 'refill request'],
    [counts.changeRequests, 'change request'],
    [counts.pendingPrescriptions, 'pending prescription'],
  ]
  return axes
    .filter(([count]) => count > 0)
    .map(([count, noun]) => `${count} ${noun}${count === 1 ? '' : 's'}`)
    .join(', ')
}

export const NotificationBell = () => {
  const navigate = useNavigate()
  const [opened, setOpened] = useState(false)
  const [isDoseSpotOpen, setIsDoseSpotOpen] = useState(false)
  const {
    notifications,
    unreadCount,
    doseSpotCounts,
    markOneRead,
    markAllRead,
    markAllReadPending,
    deleteOne,
  } = useTaskNotifications()
  // Stable per mounted bell, which is what the launch slot is keyed on.
  const claimId = useId()

  // The shell unmounts on logout, and it must not take the launch slot with it — a wedged
  // slot would refuse Prescribe for the rest of the browser session.
  useEffect(() => () => releaseDoseSpotLaunch(claimId), [claimId])

  const openTask = (
    n: Exclude<TaskNotification, PatientReadyNotification | LetterNotification>,
  ) => {
    if (!n.read) markOneRead(n.id)
    setOpened(false)
    void navigate(taskLink(n))
  }

  // The letter opens in review mode on the chart's Documents tab; the modal reads
  // the request id off the query string.
  const openLetter = (n: LetterNotification) => {
    if (!n.read) markOneRead(n.id)
    setOpened(false)
    void navigate(`/patients/${n.patientId}/documents?letterRequest=${n.requestId}`)
  }

  const openChart = (n: PatientReadyNotification) => {
    if (!n.read) markOneRead(n.id)
    setOpened(false)
    void navigate(`/patients/${n.patientId}`)
  }

  const joinVideo = (n: PatientReadyNotification) => {
    if (!n.read) markOneRead(n.id)
    setOpened(false)
    void navigate(`/call/${n.encounterId}`)
  }

  const openDoseSpot = () => {
    // DoseSpot keys the frame's patient context on a top-level-site cookie, so two open
    // launch surfaces in one tab share one context. Mantine's overlay already blocks the
    // chart→bell direction (the Prescribe modal covers the header); this guard is what stops
    // the other one — this frame open, navigate to a chart, click Prescribe.
    if (!claimDoseSpotLaunch(claimId)) {
      showNotification({
        color: 'orange',
        message:
          'Close the open DoseSpot window first — DoseSpot can only hold one prescribing session at a time.',
      })
      return
    }
    setOpened(false)
    setIsDoseSpotOpen(true)
  }

  const closeDoseSpot = () => {
    setIsDoseSpotOpen(false)
    releaseDoseSpotLaunch(claimId)
    // Deliberately no sync: there is no patient in context to sync, and a refill just
    // resolved could belong to any of them. The chart catches up on its own next view.
  }

  // The two sources are summed for the badge but never merged: a DoseSpot outage leaves the
  // task count untouched, and vice versa.
  const doseSpotPending = doseSpotTotal(doseSpotCounts)
  const badgeCount = unreadCount + doseSpotPending

  return (
    <>
      <Menu
        opened={opened}
        onChange={setOpened}
        position="bottom-end"
        width={380}
        shadow="md"
        withinPortal
      >
        <Menu.Target>
          <Indicator
            color="red"
            size={16}
            offset={4}
            label={badgeCount > 9 ? '9+' : badgeCount}
            disabled={badgeCount === 0}
          >
            <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Notifications">
              <IconBell size={20} />
            </ActionIcon>
          </Indicator>
        </Menu.Target>

        <Menu.Dropdown p={0}>
          <div className="flex items-center justify-between border-b border-b-[#EEEEEE] px-3 py-2">
            <span className="text-sm font-medium">Notifications</span>
            {unreadCount > 0 && (
              // Gated on the *task* unread count, not the badge: "Mark all read" must never
              // appear to clear a DoseSpot count, and it cannot — a transmission error still
              // pends whatever BeHuman marks.
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconChecks size={14} />}
                loading={markAllReadPending}
                onClick={markAllRead}
              >
                Mark all read
              </Button>
            )}
          </div>
          {/*
           * Pinned above the feed, and **state-derived rather than an event**: no mark-read
           * and no dismiss, because DoseSpot gives a standing count, not a log. Pushing it
           * through the feed's semantics would break three ways — "Mark all read" would clear
           * the badge while a transmission error still pends, the 24 h age cutoff would purge
           * a still-pending error, and a count going 2→3→2 would write three rows for one
           * standing fact. It clears when DoseSpot's count clears, i.e. when the prescriber acts.
           */}
          {doseSpotCounts && doseSpotPending > 0 && (
            <button
              type="button"
              onClick={openDoseSpot}
              className="flex w-full items-start gap-2 border-b border-b-[#F4F4F4] bg-orange-50/70 px-3 py-2 text-left hover:bg-orange-100/70"
            >
              <IconPrescription size={16} className="mt-0.5 shrink-0 text-orange-700" aria-hidden />
              <div className="min-w-0">
                <Text size="sm" fw={500}>
                  DoseSpot needs your attention
                </Text>
                <Text size="xs" c="dimmed" mt={2}>
                  {doseSpotBreakdown(doseSpotCounts)}
                </Text>
              </div>
            </button>
          )}

          {notifications.length === 0 ? (
            <div className="px-3 py-8 text-center">
              <Text size="sm" c="dimmed">
                You’re all caught up
              </Text>
            </div>
          ) : (
            <ScrollArea.Autosize mah={400}>
              {notifications.map((n) =>
                n.kind === 'patient-ready' ? (
                  <div
                    key={n.id}
                    className="flex flex-col gap-3 border-b border-b-[#F4F4F4] px-4 py-3.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#16A34A]/[0.12] text-[#16A34A]">
                          <IconVideo size={20} />
                        </span>
                        <Text size="lg" fw={700} c="#1F2733">
                          Patient ready
                        </Text>
                      </div>
                      <div className="flex items-center gap-2">
                        <Text size="sm" c="dimmed">
                          {formatSentAt(n.assignedAt)}
                        </Text>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label="Dismiss notification"
                          onClick={() => deleteOne(n.id)}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Text size="md" c="#1F2733">
                        {n.patientName} · {fmtTime(n.appointmentTime)}
                      </Text>
                      <Text size="sm" c="dimmed">
                        The patient completed check-in and is waiting.
                      </Text>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="default"
                        size="sm"
                        radius="md"
                        className="flex-1"
                        onClick={() => openChart(n)}
                      >
                        Open Chart
                      </Button>
                      <Button
                        color="red"
                        size="sm"
                        radius="md"
                        className="flex-1"
                        leftSection={<IconVideoFilled size={16} />}
                        onClick={() => joinVideo(n)}
                      >
                        Join Video
                      </Button>
                    </div>
                  </div>
                ) : isLetterNotification(n) ? (
                  <div
                    key={n.id}
                    className={cn([
                      'flex items-start gap-1 border-b border-b-[#F4F4F4]',
                      !n.read && 'bg-blue-50/60',
                    ])}
                  >
                    <button
                      type="button"
                      onClick={() => openLetter(n)}
                      className="flex flex-1 items-start gap-2 px-3 py-2 text-left hover:bg-black/[0.03]"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/[0.12] text-blue-600">
                        <IconSignature size={14} />
                      </span>
                      <div className="min-w-0">
                        <Text size="sm" lineClamp={3} className={cn([!n.read && 'font-medium'])}>
                          <span className="font-semibold">{n.actorName}</span>{' '}
                          {LETTER_WORDING[n.kind]} “{n.letterTitle}” for {n.patientName}
                        </Text>
                        <Text size="xs" c="dimmed" mt={2}>
                          {formatNoteTimestamp(n.assignedAt)}
                        </Text>
                      </div>
                    </button>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      className="mt-2 mr-1 shrink-0"
                      aria-label="Dismiss notification"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteOne(n.id)
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </div>
                ) : (
                  <div
                    key={n.id}
                    className={cn([
                      'flex items-start gap-1 border-b border-b-[#F4F4F4]',
                      !n.read && 'bg-blue-50/60',
                    ])}
                  >
                    <button
                      type="button"
                      onClick={() => openTask(n)}
                      className="flex flex-1 items-start gap-2 px-3 py-2 text-left hover:bg-black/[0.03]"
                    >
                      <span
                        className={cn([
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                          n.read ? 'bg-transparent' : 'bg-blue-500',
                        ])}
                      />
                      <div className="min-w-0">
                        <Text size="sm" lineClamp={2} className={cn([!n.read && 'font-medium'])}>
                          <span className="font-semibold">{n.assignedByName}</span>{' '}
                          {n.kind === 'comment' ? 'commented on the' : 'assigned you the'} task “
                          {n.taskTitle}” in {n.boardName}
                        </Text>
                        <Text size="xs" c="dimmed" mt={2}>
                          {formatNoteTimestamp(n.assignedAt)}
                        </Text>
                      </div>
                    </button>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      className="mt-2 mr-1 shrink-0"
                      aria-label="Dismiss notification"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteOne(n.id)
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </div>
                ),
              )}
            </ScrollArea.Autosize>
          )}
        </Menu.Dropdown>
      </Menu>

      {/*
       * Rendered outside the Menu, which closes on the click that opens this — a modal inside
       * the dropdown would unmount with it. Mounted only while open, so closing discards the
       * minted single-use URL.
       */}
      {isDoseSpotOpen && <DoseSpotNotificationsModal onClose={closeDoseSpot} />}
    </>
  )
}
